/*
 * cart-goal-confetti.js
 *
 * Shared confetti burst for cart blocks with a goal state (currently the
 * Free shipping bar and the Progress bar). Fires once each time a block
 * flips from goal-not-reached to goal-reached — never on initial page
 * load, and never on re-renders that keep the state unchanged.
 *
 * Contract — a participating block renders, on one element:
 *   data-block-id        the block's stable id (state is tracked per id)
 *   data-confetti-goal   "true"/"false", server-rendered goal state; the
 *                        JS never re-implements any threshold math
 *   data-confetti-config JSON {count, duration, size, shape, colors[]}
 *                        written by the block's schema settings; omitted
 *                        entirely when the block's confetti toggle is off
 *
 * Why a MutationObserver and not a cart event: this theme's main.js does
 * not dispatch `cart:rerendered` / `cart:open` (see the event audit in
 * docs/archive/PORT_INVENTORY.md) — AJAX cart updates replace drawer DOM
 * without a reliable public event. Watching the <cart-drawer> subtree
 * catches every re-render path (add-to-cart, quantity change, upsells,
 * editor saves) regardless of which code did the replacing.
 *
 * Respects prefers-reduced-motion. In the theme editor, selecting the
 * block fires a preview burst.
 */
(function () {
  'use strict';
  if (window.__cartGoalConfettiInit) return;
  window.__cartGoalConfettiInit = true;

  var GRAVITY = 900;    /* px/s^2 */
  var DRAG = 0.6;       /* fraction of horizontal velocity lost per second */
  var OPEN_DELAY = 450; /* ms — outlasts the drawer's slide-in transition */

  var lastState = {};   /* block id -> last seen goal boolean */
  var lastBurst = {};   /* block id -> timestamp, collapses double triggers */
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readConfig(el) {
    var raw = el.getAttribute('data-confetti-config');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function launch(el, cfg) {
    var rect = el.getBoundingClientRect();
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    /* Drawer overlay is z-index 1000; body-level fixed canvas above it. */
    canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:1002;';
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    var count = Math.max(1, Math.min(cfg.count || 80, 300));
    var duration = Math.max(0.5, Math.min(cfg.duration || 2, 6));
    var baseSize = Math.max(3, Math.min(cfg.size || 8, 20));
    var colors = cfg.colors && cfg.colors.length ? cfg.colors : ['#22C55E'];
    var parts = [];
    for (var i = 0; i < count; i++) {
      var angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.5; /* upward cone */
      var speed = 260 + Math.random() * 420;
      parts.push({
        x: rect.left + Math.random() * rect.width,
        y: rect.top + rect.height * 0.5,
        vx: Math.cos(angle) * speed * 0.75,
        vy: Math.sin(angle) * speed,
        size: baseSize * (0.6 + Math.random() * 0.8),
        color: colors[i % colors.length],
        shape: cfg.shape === 'mix' ? (i % 2 ? 'strips' : 'dots') : cfg.shape,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 14,       /* rad/s spin */
        flutter: Math.random() * Math.PI * 2, /* phase of the paper-flip wobble */
        life: duration * (0.6 + Math.random() * 0.4),
        age: 0
      });
    }

    var last = performance.now();
    requestAnimationFrame(function frame(now) {
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      var alive = 0;
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.age += dt;
        if (p.age >= p.life) continue;
        alive++;
        p.vy += GRAVITY * dt;
        p.vx *= 1 - DRAG * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.flutter += 8 * dt;
        var remaining = (p.life - p.age) / p.life;
        ctx.globalAlpha = remaining < 0.3 ? remaining / 0.3 : 1;
        ctx.fillStyle = p.color;
        if (p.shape === 'dots') {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 0.32, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          /* Oscillating y-scale reads as a strip of paper tumbling in 3D. */
          ctx.scale(1, 0.35 + Math.abs(Math.sin(p.flutter)) * 0.65);
          ctx.fillRect(-p.size / 2, -p.size * 0.28, p.size, p.size * 0.56);
          ctx.restore();
        }
      }
      if (alive) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    });
  }

  function fire(el) {
    var cfg = readConfig(el);
    if (!cfg || prefersReducedMotion()) return;
    var id = el.getAttribute('data-block-id') || '';
    var now = Date.now();
    if (lastBurst[id] && now - lastBurst[id] < 800) return;
    lastBurst[id] = now;
    var tries = 0;
    setTimeout(function check() {
      /* The block may re-render between detection and burst — re-resolve
         by block id so we measure the live element, not a detached one. */
      var live = document.querySelector('[data-confetti-goal][data-block-id="' + id + '"]') || el;
      var rect = live.getBoundingClientRect();
      var visible = rect.width > 0 && rect.height > 0;
      var drawer = live.closest && live.closest('cart-drawer');
      if (drawer && !drawer.classList.contains('active')) visible = false;
      if (visible && live.checkVisibility) visible = live.checkVisibility();
      if (visible) {
        launch(live, readConfig(live) || cfg);
      } else if (++tries < 10) {
        setTimeout(check, 150); /* waiting on the drawer to open; ~1.5s cap */
      }
    }, OPEN_DELAY);
  }

  function scan() {
    document.querySelectorAll('[data-confetti-goal][data-block-id]').forEach(function (el) {
      var id = el.getAttribute('data-block-id');
      var reached = el.getAttribute('data-confetti-goal') === 'true';
      var seen = Object.prototype.hasOwnProperty.call(lastState, id);
      var was = lastState[id];
      lastState[id] = reached;
      if (seen && was === false && reached) fire(el);
    });
    /* Attach the re-render watcher to any drawer not yet observed. */
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      if (!observed || observed.has(drawer)) return;
      observed.add(drawer);
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          scan();
        });
      }).observe(drawer, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  if (window.Shopify && window.Shopify.designMode) {
    /* Section reloads replace the <cart-drawer> element itself — re-seed
       state and re-attach the observer to the new element. */
    document.addEventListener('shopify:section:load', scan);
    /* Merchant preview: selecting the block in the editor fires a burst.
       The config element may be the selected block root itself or a
       descendant, depending on where the block puts shopify_attributes. */
    document.addEventListener('shopify:block:select', function (e) {
      var t = e.target;
      if (!t || !t.querySelector) return;
      var el = t.matches && t.matches('[data-confetti-config]') ? t : t.querySelector('[data-confetti-config]');
      if (el) fire(el);
    });
  }
})();
